// src/store/vendorAssemblyStore.ts
import { create } from "zustand";
import { useUserStore } from "./userStore";
import {
  fetchVendorMap,
  assignAssemblyToVendor,
  deactivateAssemblyForVendor,
  type VendorAssemblyMapDto,
  type AssemblyBriefDto,
} from "../apiServices/vendorAssembliesApi";

function resolveOem(): string {
  const s: any = useUserStore.getState();
  const legacy = s.currentOEM?.oemName as string | undefined;
  if (legacy && legacy.trim()) return legacy;
  const id = s.user?.oemId;
  if (id) return `Oem${id}`;
  const envDefault = (import.meta as any)?.env?.VITE_DEFAULT_OEM as string | undefined;
  return envDefault && envDefault.trim() ? envDefault : "Oem1";
}

type VendorAssemblyStore = {
  vendorId: number | null;
  map: VendorAssemblyMapDto | null;
  loading: boolean;
  error: string | null;
  hasLoadedOnce: boolean;

  setVendor: (vendorId: number | null) => void;
  load: (vendorId?: number) => Promise<void>;
  assign: (finalAssemblyId: number) => Promise<void>;
  deactivate: (finalAssemblyId: number) => Promise<void>;
  clear: () => void;
};

export const useVendorAssemblyStore = create<VendorAssemblyStore>((set, get) => ({
  vendorId: null,
  map: null,
  loading: false,
  error: null,
  hasLoadedOnce: false,

  setVendor(vendorId) {
    set({ vendorId });
  },

  async load(vendorIdParam) {
    const vendorId = vendorIdParam ?? get().vendorId;
    if (!vendorId) {
      set({ map: null, hasLoadedOnce: true, loading: false });
      return;
    }
    set({ loading: true, error: null });
    try {
      const oem = resolveOem();
      const data = await fetchVendorMap(oem, vendorId);
      set({ map: data, loading: false, hasLoadedOnce: true });
    } catch (e: any) {
      set({ map: null, loading: false, hasLoadedOnce: true, error: e?.message ?? "Failed to load mapping" });
    }
  },

  async assign(finalAssemblyId) {
    const vendorId = get().vendorId;
    if (!vendorId) return;
    const oem = resolveOem();
    const res = await assignAssemblyToVendor(oem, vendorId, finalAssemblyId);
    if (res?.statusCode !== 200) {
      set({ error: res?.message ?? "Failed to add model to vendor" });
      return;
    }

    // move item from available -> assigned locally
    set((state) => {
      if (!state.map) return {};
      const available = state.map.available.filter(a => a.finalAssemblyId !== finalAssemblyId);
      const moved = state.map.available.find(a => a.finalAssemblyId === finalAssemblyId);
      const assigned: AssemblyBriefDto[] = [{ ...(moved || { finalAssemblyId }), approvedOn: new Date().toISOString(), isVisible: true }, ...state.map.assigned];
      return { map: { ...state.map, available, assigned } };
    });
  },

  async deactivate(finalAssemblyId) {
    const vendorId = get().vendorId;
    if (!vendorId) return;
    const oem = resolveOem();
    const res = await deactivateAssemblyForVendor(oem, vendorId, finalAssemblyId);
    if (res?.statusCode !== 200) {
      set({ error: res?.message ?? "Failed to deactivate model for vendor" });
      return;
    }

    // move item from assigned -> available locally
    set((state) => {
      if (!state.map) return {};
      const assigned = state.map.assigned.filter(a => a.finalAssemblyId !== finalAssemblyId);
      const moved = state.map.assigned.find(a => a.finalAssemblyId === finalAssemblyId);
      const available: AssemblyBriefDto[] = [{ ...(moved || { finalAssemblyId }), approvedOn: null, isVisible: false }, ...state.map.available];
      return { map: { ...state.map, available, assigned } };
    });
  },

  clear: () => set({ vendorId: null, map: null, loading: false, error: null, hasLoadedOnce: false }),
}));
